import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AddCaseIcon,
  AddDomainIcon,
  Button,
  Flags,
  GroupAddIcon,
  PlusIcon,
  PopoverMenu,
  UploadFileIcon,
  useFlag,
} from 'ui';
import { CreateOrganizationModal } from '@components/CreateOrganizationModal';
import { InvitationModalsOrchestrator } from '@components/InvitationModalsOrchestrator';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { PATHS } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { FirebaseAuth } from 'services/FirebaseService/auth';

const AdminRoles = [UserRole.Admin, UserRole.Owner];

export function AddOptionsButton() {
  const isUploadClaimsFileEnabled = useFlag(Flags.uploadClaimsFile);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const navigate = useNavigate();
  const anchorEl = useRef<HTMLButtonElement>(null);

  const { getUserRole, selectedOrganization, selectedCase } = useUserOrgCase();

  const organizationRole = getUserRole(RoleLevel.organization);
  const caseRole = getUserRole(RoleLevel.case);

  const isCurrentOrgAdmin =
    organizationRole !== undefined && AdminRoles.includes(organizationRole);

  const isCurrentCaseAdmin =
    caseRole !== undefined && AdminRoles.includes(caseRole);

  const { data: isSuperAdmin = false } = useQuery({
    queryKey: ['isSuperAdmin'],
    queryFn: () => FirebaseAuth.isSuperAdmin(),
  });

  const [inviteOrgModalOpen, setInviteOrgModalOpen] = useState(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);

  const menuOptions = useMemo(
    () => [
      {
        onClick: () => setCreateOrgModalOpen(true),
        icon: <AddDomainIcon />,
        iconPosition: 'start' as const,
        title: 'Create Organization',
        visible: isSuperAdmin,
      },
      {
        divider: true,
        visible: isSuperAdmin,
      },
      {
        onClick: () => {
          navigate(
            `${PATHS.PRIVATE_BASE.newCaseCreation}?orgId=${selectedOrganization.organizationID}`,
            { state: { previousUrl: location.pathname } },
          );
        },
        icon: <AddCaseIcon />,
        iconPosition: 'start' as const,
        title: 'Create New Case',
        visible: isCurrentOrgAdmin,
      },
      {
        divider: true,
        visible: isCurrentOrgAdmin,
      },
      {
        onClick: () => setInviteOrgModalOpen(true),
        iconPosition: 'start' as const,
        icon: <AddDomainIcon />,
        title: 'Add Organization Member',
        visible: isCurrentOrgAdmin,
      },
      {
        onClick: () => setInviteUserModalOpen(true),
        iconPosition: 'start' as const,
        icon: <GroupAddIcon />,
        title: 'Add Case Member',
        visible: isCurrentCaseAdmin,
      },
      {
        divider: true,
        visible: isCurrentOrgAdmin,
      },
      {
        onClick: () => {
          navigate(
            `${PATHS.PRIVATE_BASE.uploadClaimsFile}?orgId=${selectedOrganization.organizationID}&caseId=${selectedCase.caseID}`,
            { state: { previousUrl: location.pathname } },
          );
        },
        icon: <UploadFileIcon />,
        iconPosition: 'start' as const,
        visible: isCurrentCaseAdmin,
        title: 'Upload Claims File',
        disabled: !isUploadClaimsFileEnabled,
      },
    ],
    [
      isCurrentCaseAdmin,
      isCurrentOrgAdmin,
      isSuperAdmin,
      isUploadClaimsFileEnabled,
      navigate,
      selectedOrganization.organizationID,
      selectedCase?.caseID,
    ],
  );

  return (
    <>
      <Button
        aria-label="Add Options Dropdown"
        color="neutral"
        onClick={() => {
          setIsPopoverOpen((prev) => !prev);
        }}
        ref={anchorEl}
        startIcon={<PlusIcon />}
        variant="text"
      />
      <PopoverMenu
        anchorEl={anchorEl.current}
        isOpen={isPopoverOpen}
        menuItems={menuOptions}
        onClose={() => {
          setIsPopoverOpen(false);
        }}
      />
      <InvitationModalsOrchestrator
        inviteOrgModalOpen={inviteOrgModalOpen}
        inviteUserModalOpen={inviteUserModalOpen}
        setInviteOrgModalOpen={setInviteOrgModalOpen}
        setInviteUserModalOpen={setInviteUserModalOpen}
      />
      <CreateOrganizationModal
        onClose={() => setCreateOrgModalOpen(false)}
        open={createOrgModalOpen}
      />
    </>
  );
}
